import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

function KPIPill({ label, value, color, small }) {
  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 8, padding: small ? '8px 12px' : '10px 14px',
      minWidth: small ? 90 : 110, flex: '1 1 0', textAlign: 'center',
    }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: small ? 18 : 22, fontWeight: 700, color: color || 'var(--text)', fontFamily: 'DM Mono, monospace' }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function KPIGroup({ title, icon, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
        {icon} {title}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

const DAYS_OPTIONS = [7, 30, 90];

export default function LeadOverview() {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [kpi, setKpi]             = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [days, setDays]           = useState(30);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      get(`/lead/overview?club=${currentClub.id}&days=${days}`),
      get(`/lead/assignments?club=${currentClub.id}`),
    ])
      .then(([kpiData, assignData]) => {
        setKpi(kpiData);
        setAssignments(assignData?.assignments || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id, days]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;

  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load overview — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );

  if (!kpi) return null;

  const tier = kpi.tierBreakdown || {};
  const pipelines = kpi.pipelineBreakdown || [];
  const outcomes = kpi.outcomeBreakdown || [];

  return (
    <div style={{ padding: '16px 0' }}>

      {/* ── Date range selector ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {DAYS_OPTIONS.map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              background: days === d ? 'var(--yellow)' : 'var(--surface-2)',
              color: days === d ? '#000' : 'var(--text-muted)',
              border: '1px solid var(--border)', borderRadius: 6,
              padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Last {d}d
          </button>
        ))}
      </div>

      {/* ── Two-column layout ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>

        {/* LEFT — KPI groups */}
        <div>
          <KPIGroup title="Pipeline Summary" icon="📋">
            <KPIPill label="Active Prospects"    value={kpi.activeProspects} />
            <KPIPill label="Eligible Today"      value={kpi.todaysContacts}     color="var(--yellow)" />
            <KPIPill label="Contacted Today"     value={kpi.contactsMadeToday}  color="var(--green)" />
            <KPIPill label="Converted"           value={kpi.converted}          color="var(--green)" />
            <KPIPill label="Conv. %"             value={`${kpi.conversionRate}%`} />
            <KPIPill label="DNC"                 value={kpi.dnc}                color="var(--red)" />
          </KPIGroup>

          <KPIGroup title="Tier Breakdown" icon="🔥">
            <KPIPill label="🔥 Hot"   value={tier.hot}  color="var(--red)"    small />
            <KPIPill label="🌡 Warm"  value={tier.warm} color="var(--orange)" small />
            <KPIPill label="🧊 Cool"  value={tier.cool} color="var(--yellow)" small />
            <KPIPill label="❄️ Cold"  value={tier.cold} color="var(--text-muted)" small />
          </KPIGroup>

          {/* Pipeline Breakdown table */}
          {pipelines.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
                📊 Pipeline Breakdown
              </div>
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Pipeline', 'Total', 'High', 'Active', 'Conv.%', 'Avg Score'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Pipeline' ? 'left' : 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pipelines.map((p, i) => (
                      <tr key={p.pipeline} style={{ borderBottom: i < pipelines.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{
                          {'lead_engine': 'Lead Engine', 'win_back_non_member': 'Win Back — Non-Member', 'win_back_past_member': 'Win Back — Past Member', 'trial_ready': 'Trial Ready'}[p.pipeline] || p.pipeline
                        }</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace' }}>{p.total}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace', color: 'var(--red)' }}>{p.highCount}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace', color: 'var(--yellow)' }}>{p.activeToday}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace', color: p.convPct > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{p.convPct}%</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace' }}>{p.avgScore}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Outcome Breakdown table */}
          {outcomes.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
                📈 Outcome Breakdown
              </div>
              <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Outcome', 'Count'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Outcome' ? 'left' : 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {outcomes.map((o, i) => (
                      <tr key={o.outcome} style={{ borderBottom: i < outcomes.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{o.outcome}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontFamily: 'DM Mono, monospace' }}>{o.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — summary panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
              📊 Quick Stats · Last {days}d
            </div>
            {[
              { label: 'Active Prospects',  value: kpi.activeProspects },
              { label: 'Eligible Today',    value: kpi.todaysContacts,    color: 'var(--yellow)' },
              { label: 'Contacted Today',   value: kpi.contactsMadeToday, color: 'var(--green)' },
              { label: 'Converted',         value: kpi.converted,         color: 'var(--green)' },
              { label: 'Conversion Rate',   value: `${kpi.conversionRate}%` },
              { label: 'DNC Suppressed',    value: kpi.dnc,               color: 'var(--red)' },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'DM Mono, monospace', color: s.color || 'var(--text)' }}>{s.value}</span>
              </div>
            ))}
          </div>

          {/* Tier visual bar */}
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
              🔥 Tier Distribution
            </div>
            {[
              { label: '🔥 Hot',  value: tier.hot,  color: 'var(--red)',      bg: 'rgba(220,38,38,0.2)' },
              { label: '🌡 Warm', value: tier.warm, color: '#f97316',         bg: 'rgba(234,88,12,0.2)' },
              { label: '🧊 Cool', value: tier.cool, color: 'var(--yellow)',   bg: 'rgba(234,179,8,0.2)' },
              { label: '❄️ Cold', value: tier.cold, color: 'var(--text-muted)', bg: 'var(--surface-3)' },
            ].map(t => {
              const total = (tier.hot || 0) + (tier.warm || 0) + (tier.cool || 0) + (tier.cold || 0);
              const pct = total > 0 ? Math.round((t.value || 0) / total * 100) : 0;
              return (
                <div key={t.label} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: t.color }}>{t.label}</span>
                    <span style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: t.color }}>{t.value || 0} ({pct}%)</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--surface-3)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: t.color, borderRadius: 3 }} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Assignments panel */}
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
              👤 Lead Assignments
            </div>
            {assignments.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No leads assigned yet.
              </div>
            ) : (
              assignments.map(a => (
                <div key={a.userId} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{a.name}</span>
                    <span style={{
                      background: 'rgba(59,130,246,0.15)', color: '#60a5fa',
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      fontFamily: 'DM Mono, monospace',
                    }}>{a.count} leads</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {a.leads.slice(0, 3).map(l => (
                      <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)' }}>
                        <span style={{ textTransform: 'capitalize' }}>{l.name}</span>
                        <span style={{ fontFamily: 'DM Mono, monospace', color: l.score >= 70 ? 'var(--red)' : l.score >= 45 ? '#f97316' : 'var(--text-muted)' }}>
                          {l.score}
                        </span>
                      </div>
                    ))}
                    {a.leads.length > 3 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        +{a.leads.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
